import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Box,
  Typography,
  Alert,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { PlanosGrupo } from '../../types';
import { planosGruposService } from '../../services/planosGruposService';

interface PlanosGruposModalProps {
  open: boolean;
  onClose: () => void;
  onGruposChange?: () => void;
}

const PlanosGruposModal: React.FC<PlanosGruposModalProps> = ({
  open,
  onClose,
  onGruposChange,
}) => {
  const [grupos, setGrupos] = useState<PlanosGrupo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState<PlanosGrupo | null>(null);
  const [titulo, setTitulo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<PlanosGrupo | null>(null);

  const carregarGrupos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await planosGruposService.getAll();
      if (response.success && response.data) {
        setGrupos(response.data);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar grupos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      carregarGrupos();
    }
  }, [open, carregarGrupos]);

  const handleNovo = () => {
    setEditando(null);
    setTitulo('');
    setFormOpen(true);
  };

  const handleEditar = (grupo: PlanosGrupo) => {
    setEditando(grupo);
    setTitulo(grupo.titulo);
    setFormOpen(true);
  };

  const handleSalvar = async () => {
    if (!titulo.trim()) return;

    try {
      setSalvando(true);
      setError(null);

      if (editando) {
        const response = await planosGruposService.update(editando.id, { titulo: titulo.trim() });
        if (response.success && response.data) {
          setGrupos((prev) =>
            prev.map((g) => (g.id === editando.id ? response.data! : g))
          );
        }
      } else {
        const response = await planosGruposService.create({ titulo: titulo.trim() });
        if (response.success && response.data) {
          setGrupos((prev) => [...prev, response.data!]);
        }
      }

      setFormOpen(false);
      setEditando(null);
      setTitulo('');
      onGruposChange?.();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar grupo');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async () => {
    if (!deleteConfirm) return;

    try {
      setSalvando(true);
      setError(null);
      await planosGruposService.delete(deleteConfirm.id);
      setGrupos((prev) => prev.filter((g) => g.id !== deleteConfirm.id));
      setDeleteConfirm(null);
      onGruposChange?.();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao excluir grupo');
    } finally {
      setSalvando(false);
    }
  };

  const handleClose = () => {
    if (!salvando) {
      setError(null);
      setFormOpen(false);
      setDeleteConfirm(null);
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <FolderIcon color="primary" />
              <Typography variant="h6">Gerenciar Grupos</Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleNovo}
            >
              Novo
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell align="center" width={80}>Qtde</TableCell>
                  <TableCell align="center" width={100}>Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : grupos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Nenhum grupo cadastrado
                    </TableCell>
                  </TableRow>
                ) : (
                  grupos.map((grupo) => (
                    <TableRow key={grupo.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {grupo.titulo}
                          {grupo.padrao && (
                            <Chip label="Padrão" size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">{grupo.qtde ?? 0}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleEditar(grupo)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!grupo.padrao && (
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteConfirm(grupo)}
                              disabled={(grupo.qtde ?? 0) > 0}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Formulário Novo/Editar */}
      <Dialog open={formOpen} onClose={() => !salvando && setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editando ? 'Editar Grupo' : 'Novo Grupo'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Título"
            fullWidth
            size="small"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            sx={{ mt: 1 }}
            placeholder="Ex: Corporativo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSalvar}
            disabled={salvando || !titulo.trim()}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação de exclusão */}
      <Dialog open={!!deleteConfirm} onClose={() => !salvando && setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza de que deseja excluir o grupo "{deleteConfirm?.titulo}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={handleExcluir} disabled={salvando}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlanosGruposModal;
